// Vendored copy of the server match-core (Phase 0, Task 7).
//
// This is the canonical reducer for the CLIENT. It is a byte-identical vendored
// copy of the server's `commands.js` + `reducer.js` + `wire.js` (a drift guard
// in `matchCore.parity.test.js` asserts the `applyCommand` source matches the
// server source verbatim). The "build once, no drift" rule: the client and the
// server fold inbound state with the same reducer, so a Diff applied on the
// client lands on byte-identical state.
//
// Do NOT edit `applyCommand` here independently. Change the server source and
// re-vendor, or the parity test fails.
import { produceWithPatches, applyPatches, enablePatches } from 'immer';
import { resetCardOnLeavePlay } from '../engine/core/lifecycle';
import { mergeUnitHandViews } from '../engine/core/unitHands';
import { untapStep, healStep } from '../engine/core/turnSteps';

enablePatches();

// Re-exported so the optimistic command client (Phase 1, Task 4) can roll back
// an unconfirmed prediction by applying the retained inverse patches.
export { applyPatches };

// --- commands.js (vendored) ---
export const NOOP = 'NOOP';
export const SET_PHASE = 'SET_PHASE';
export const PASS_TURN = 'PASS_TURN';
export const ROLL_INITIATIVE = 'ROLL_INITIATIVE';
export const SET_FIRST_PLAYER = 'SET_FIRST_PLAYER';
export const SEED_PHASE = 'SEED_PHASE';
export const ROLL_SEAT = 'ROLL_SEAT';
export const ADVANCE_START_STEP = 'ADVANCE_START_STEP';
export const SEED_RESOURCES = 'SEED_RESOURCES';
export const SET_LIFE = 'SET_LIFE';
export const SET_MANA = 'SET_MANA';
export const SET_THRESHOLDS = 'SET_THRESHOLDS';
export const DRAW = 'DRAW';
export const MOVE_TO_ZONE = 'MOVE_TO_ZONE';
export const SHUFFLE = 'SHUFFLE';
export const PILE_REORDER = 'PILE_REORDER';
export const SET_REVEALED = 'SET_REVEALED';
export const SET_PILE_REVEALED = 'SET_PILE_REVEALED';
export const SET_PREGAME_DONE = 'SET_PREGAME_DONE';
export const SET_MULLIGAN_DONE = 'SET_MULLIGAN_DONE';
export const SET_TAPPED = 'SET_TAPPED';
export const SET_CONTROLLER = 'SET_CONTROLLER';
export const SET_CARD_STATS = 'SET_CARD_STATS';
export const SET_STATUSES = 'SET_STATUSES';
export const SET_COUNTERS = 'SET_COUNTERS';
export const SET_SUBSURFACE = 'SET_SUBSURFACE';
export const SET_EQUIPMENT = 'SET_EQUIPMENT';
export const SET_CARD_FLAGS = 'SET_CARD_FLAGS';
export const SET_ANIMATE = 'SET_ANIMATE';
export const SET_IDENTITY = 'SET_IDENTITY';
export const SET_POSITION = 'SET_POSITION';
export const SET_UNIT_HANDS = 'SET_UNIT_HANDS';
export const SET_DISABLED_AUTOMATIONS = 'SET_DISABLED_AUTOMATIONS';

// Wire-only intent (not a reducer command): the client emits REQUEST_ROLL and
// the server substitutes it for a server-generated ROLL_INITIATIVE before the
// reducer runs (commandHandler arbitration). Defined here so the client emits
// the exact type string the server matches on.
export const REQUEST_ROLL = 'REQUEST_ROLL';

// --- reducer.js (vendored) ---
class UnknownCommand extends Error {
  constructor(type) {
    super(`UnknownCommand: ${type}`);
    this.name = 'UnknownCommand';
    this.type = type;
  }
}

// The named per-player pile zones (mirrors matchStateShape ZONE_NAMES). A
// move whose destination is one of these lands in `piles[owner][zone]`; any
// other zone ('hand' has its own array, board zones like 'play' carry no array).
const PILE_ZONES = new Set(['spellbook', 'atlas', 'cemetery', 'banished', 'collection', 'tokens', 'storyline']);

// Remove `cardId` from whatever order-array currently holds it (its owner's hand
// when zone==='hand', or its owner's pile when zone is a named pile). Board zones
// carry no array, so nothing to splice. Idempotent: a missing id is a no-op.
function detachFromCurrentZone(draft, card) {
  if (!card) return;
  const owner = card.owner;
  const zone = card.zone;
  if (zone === 'held') {
    const ownerId = card.castLockOwnerId ?? card._stolenBy;
    const entry = draft.unitHands?.[ownerId];
    if (entry) {
      entry.spells = entry.spells.filter((s) => s.id !== card.instanceId);
      if (!entry.spells.length) delete draft.unitHands[ownerId];
    }
    return;
  }
  let arr = null;
  if (zone === 'hand') arr = draft.hands[owner];
  else if (PILE_ZONES.has(zone)) arr = draft.piles[owner]?.[zone];
  if (!Array.isArray(arr)) return;
  const idx = arr.indexOf(card.instanceId);
  if (idx !== -1) arr.splice(idx, 1);
}

export function applyCommand(draft, command) {
  switch (command.type) {
    case NOOP:
      return;
    case SET_PHASE:
      draft.phase.matchPhase = command.payload.matchPhase;
      return;
    case SEED_PHASE: {
      // Seed-ONCE: the phase is adopted from a client exactly one time (a
      // legacy match upgrading into phase authority). Once seeded. Or once
      // SET_FIRST_PLAYER has made the phase authoritative. A late/stale seed
      // is a no-op. Without this gate the first turn-pass raced: PASS_TURN
      // flipped the turn, then the passer's pre-flip seed clobbered it back
      // (both clients stuck on the old turn + double draw prompts).
      if (draft.seeded || draft.rollOff?.firstPlayerSeat) return;
      const p = command.payload || {};
      if (p.matchPhase !== undefined) draft.phase.matchPhase = p.matchPhase;
      if (p.currentTurn !== undefined) draft.phase.currentTurn = p.currentTurn;
      if (p.turnNumbers !== undefined) draft.phase.turnNumbers = { ...p.turnNumbers };
      if (p.currentPhase !== undefined) draft.phase.currentPhase = p.currentPhase;
      if (p.rollOff !== undefined) draft.rollOff = { ...p.rollOff };
      draft.seeded = true;
      return;
    }
    case ROLL_INITIATIVE: {
      const { p1Roll, p2Roll } = command.payload;
      draft.rollOff.p1Roll = p1Roll;
      draft.rollOff.p2Roll = p2Roll;
      draft.rollOff.winnerSeat =
        p1Roll === p2Roll ? null : p1Roll > p2Roll ? 'p1' : 'p2';
      if (draft.rollOff.firstPlayerSeat === undefined) {
        draft.rollOff.firstPlayerSeat = null;
      }
      return;
    }
    case ROLL_SEAT: {
      const { seat, value } = command.payload;
      draft.rollOff[seat === 'p1' ? 'p1Roll' : 'p2Roll'] = value;
      const { p1Roll, p2Roll } = draft.rollOff;
      if (p1Roll != null && p2Roll != null) {
        if (p1Roll === p2Roll) {
          draft.rollOff.p1Roll = null;
          draft.rollOff.p2Roll = null;
          draft.rollOff.winnerSeat = null;
        } else {
          draft.rollOff.winnerSeat = p1Roll > p2Roll ? 'p1' : 'p2';
        }
      } else {
        draft.rollOff.winnerSeat = null;
      }
      if (draft.rollOff.firstPlayerSeat === undefined) {
        draft.rollOff.firstPlayerSeat = null;
      }
      return;
    }
    case SET_FIRST_PLAYER: {
      const { seat } = command.payload;
      draft.rollOff.firstPlayerSeat = seat;
      draft.phase.currentTurn = seat;
      // This BEGINS the first player's turn 1. PASS_TURN only ever increments
      // the INCOMING seat, so without seeding the first player here their
      // counter stays one behind for the whole game. Making their 2nd turn
      // read turnNumber 1. Seed it to 1 so turn N == turnNumber N.
      if (!draft.phase.turnNumbers) draft.phase.turnNumbers = { p1: 0, p2: 0 };
      draft.phase.turnNumbers[seat] = 1;
      // Global turn 1 begins. The only turn that skips the draw.
      draft.phase.globalTurn = 1;
      // Advance the game-start sequence to pre-game abilities. Do NOT flip
      // matchPhase to 'playing' here. The opening-draw phase owns that
      // transition (spec §17). Writing 'playing' early clobbered the
      // in-progress opening draw.
      draft.phase.startStep = 'preGame';
      return;
    }
    case ADVANCE_START_STEP: {
      draft.phase.startStep = command.payload.to;
      if (command.payload.matchPhase !== undefined) {
        draft.phase.matchPhase = command.payload.matchPhase;
      }
      return;
    }
    case SEED_RESOURCES: {
      for (const seat of ['p1', 'p2']) {
        const r = command.payload?.[seat];
        if (!r) continue;
        if (r.life !== undefined) draft.players[seat].life = r.life;
        if (r.mana !== undefined) draft.players[seat].mana = r.mana;
        if (r.thresholds !== undefined) draft.players[seat].thresholds = { ...r.thresholds };
        draft.players[seat].deathsDoorTurnKey = r.deathsDoorTurnKey ?? null;
      }
      draft.seededResources = true;
      return;
    }
    case SET_LIFE: {
      const { seat, life, deathsDoorTurnKey } = command.payload;
      draft.players[seat].life = life;
      draft.players[seat].deathsDoorTurnKey = deathsDoorTurnKey ?? null;
      return;
    }
    case SET_MANA: {
      const { seat, mana } = command.payload;
      draft.players[seat].mana = mana;
      return;
    }
    case SET_THRESHOLDS: {
      const { seat, thresholds } = command.payload;
      draft.players[seat].thresholds = { ...thresholds };
      return;
    }
    case DRAW: {
      const { seat, pileName } = command.payload;
      const count = command.payload.count ?? 1;
      const pile = draft.piles[seat]?.[pileName];
      if (!Array.isArray(pile)) return;
      const n = Math.min(count, pile.length);
      for (let i = 0; i < n; i++) {
        const id = pile.shift();
        draft.hands[seat].push(id);
        if (draft.cards[id]) draft.cards[id].zone = 'hand';
      }
      if (n > 0) dischargeDrawObligation(draft, seat, pileName);
      return;
    }
    case MOVE_TO_ZONE: {
      const { cardId, toZone, index, faceDown, manualDraw } = command.payload;
      const card = draft.cards[cardId];
      if (!card) return;
      const priorZone = card.zone;
      detachFromCurrentZone(draft, card);
      const owner = card.owner;
      if (toZone === 'hand') {
        draft.hands[owner].push(cardId);
        // Only the player's own drag-draw (marked by the client) pays the
        // start-phase draw. An ability's deck→hand move (the avatar's "draw a
        // site", Sorcerer's spell draw) rides this command unmarked and must
        // leave the obligation standing.
        if (manualDraw) dischargeDrawObligation(draft, owner, priorZone);
      } else if (PILE_ZONES.has(toZone)) {
        // Lazily init the destination array: a match resumed from a snapshot
        // written before a zone existed (e.g. 'storyline') has no array yet.
        if (!Array.isArray(draft.piles[owner][toZone])) draft.piles[owner][toZone] = [];
        const dest = draft.piles[owner][toZone];
        if (typeof index === 'number' && index >= 0 && index <= dest.length) {
          dest.splice(index, 0, cardId);
        } else {
          dest.push(cardId);
        }
      }
      // Board zones ('play') carry no order-array. Only the registry zone moves.
      card.zone = toZone;
      // Leave-play reset: any card landing in a resting zone (hand or a pile,
      // but NOT a transient storyline dwell) becomes a clean copy. Damage,
      // aura delta, statuses, tapped, etc. wiped. A card pulled back from the
      // cemetery must no longer count as defeated; it re-enters fresh. Runs
      // BEFORE faceDown so an explicit face-down move wins over the reset's wipe.
      if (toZone !== 'play' && toZone !== 'storyline') resetCardOnLeavePlay(card);
      // A special spellcaster leaving the realm flushes its unit hand (its
      // remaining spells were already routed to a pile via their own moves).
      if (toZone !== 'play' && toZone !== 'storyline' && draft.unitHands?.[cardId]) delete draft.unitHands[cardId];
      if (faceDown !== undefined) card.faceDown = faceDown;
      return;
    }
    case SHUFFLE: {
      const { seat, pileName, order } = command.payload;
      if (draft.piles[seat]?.[pileName] && Array.isArray(order)) {
        draft.piles[seat][pileName] = [...order];
      }
      return;
    }
    // A player's own arrangement of an ordered draw pile (a scry). Applies the
    // order verbatim, exactly as SHUFFLE does. The difference between the two
    // is entirely in the gate: the handler proves this is a permutation of the
    // pile it already holds before the command ever reaches here.
    case PILE_REORDER: {
      const { seat, zone, order } = command.payload;
      if (draft.piles[seat]?.[zone] && Array.isArray(order)) {
        draft.piles[seat][zone] = [...order];
      }
      return;
    }
    case SET_REVEALED: {
      const { cardId, seats, faceDown } = command.payload;
      const card = draft.cards[cardId];
      if (!card) return;
      if (Array.isArray(seats)) {
        if (!Array.isArray(card.revealedTo)) card.revealedTo = [];
        for (const s of seats) {
          if (!card.revealedTo.includes(s)) card.revealedTo.push(s);
        }
      }
      if (faceDown !== undefined) card.faceDown = faceDown;
      return;
    }
    case SET_PILE_REVEALED: {
      const { seat, pileName, revealed } = command.payload;
      if (!draft.revealedPiles[seat]) draft.revealedPiles[seat] = {};
      if (revealed) draft.revealedPiles[seat][pileName] = true;
      else delete draft.revealedPiles[seat][pileName];
      return;
    }
    case SET_PREGAME_DONE: {
      const { seat } = command.payload;
      if (!draft.phase.preGameDone) draft.phase.preGameDone = { p1: false, p2: false };
      draft.phase.preGameDone[seat] = true;
      if (draft.phase.preGameDone.p1 && draft.phase.preGameDone.p2 && draft.phase.startStep === 'preGame') {
        draft.phase.startStep = 'openingDraw';
      }
      return;
    }
    case SET_MULLIGAN_DONE: {
      const { seat } = command.payload;
      if (!draft.phase.mulliganDone) draft.phase.mulliganDone = { p1: false, p2: false };
      draft.phase.mulliganDone[seat] = true;
      // Game truly begins only when BOTH seats finish their mulligan. Until then
      // matchPhase stays out of 'playing', so neither client opens its play gate.
      if (draft.phase.mulliganDone.p1 && draft.phase.mulliganDone.p2 && draft.phase.startStep === 'openingDraw') {
        draft.phase.startStep = 'done';
        draft.phase.matchPhase = 'playing';
      }
      return;
    }
    case SET_TAPPED: {
      const card = draft.cards[command.payload.cardId];
      if (card) card.tapped = command.payload.tapped;
      return;
    }
    case SET_CONTROLLER: {
      const card = draft.cards[command.payload.cardId];
      if (!card) return;
      // `rotated` is the client's "controlled by p2" flag, so controller follows
      // it directly (rotated ⇒ p2 controls; else p1). Independent of owner.
      card.rotated = command.payload.rotated;
      card.controller = command.payload.rotated ? 'p2' : 'p1';
      return;
    }
    case SET_CARD_STATS: {
      const card = draft.cards[command.payload.cardId];
      if (!card) return;
      const p = command.payload;
      if (p.currentAttack !== undefined) card.currentAttack = p.currentAttack;
      if (p.currentLife !== undefined) card.currentLife = p.currentLife;
      if (p.movementBonus !== undefined) card.movementBonus = p.movementBonus;
      if (p.manualLifeDelta !== undefined) card.manualLifeDelta = p.manualLifeDelta;
      if (p.auraStatDelta !== undefined) card._auraStatDelta = p.auraStatDelta;
      if (p.auraStatuses !== undefined) card._auraStatuses = p.auraStatuses;
      if (p.auraSuppressedKeywords !== undefined) card._auraSuppressedKeywords = p.auraSuppressedKeywords;
      if (p.printedKeywords !== undefined) card._printedKeywords = p.printedKeywords;
      if (p.suppressedKeywords !== undefined) card._suppressedKeywords = p.suppressedKeywords;
      return;
    }
    case SET_STATUSES: {
      const card = draft.cards[command.payload.cardId];
      if (!card) return;
      const statuses = command.payload.statuses;
      // A Charge grant after summon (manual status ring, effect) lifts
      // summoning sickness, mirroring engine/core applyLateCharge. Without
      // this the token lands but the flag keeps blocking attack/move taps.
      if (Array.isArray(statuses) && statuses.includes('charge') && card._hasSummoningSickness) {
        delete card._hasSummoningSickness;
      }
      card.statuses = statuses;
      return;
    }
    case SET_COUNTERS: {
      const card = draft.cards[command.payload.cardId];
      if (card) card.counters = command.payload.counters;
      return;
    }
    case SET_SUBSURFACE: {
      const card = draft.cards[command.payload.cardId];
      if (card) card.subsurface = command.payload.subsurface;
      return;
    }
    case SET_EQUIPMENT: {
      const card = draft.cards[command.payload.cardId];
      if (card) card.equippedCards = command.payload.equippedCards;
      return;
    }
    case SET_CARD_FLAGS: {
      const card = draft.cards[command.payload.cardId];
      if (!card) return;
      const p = command.payload;
      for (const key of ['skipNextUntap', 'skipNextTurn', 'castLockOwnerId', 'abilitiesUsedThisTurn', 'abilitiesUsedThisGame', 'dragonlordCompanion', 'percevalTargetCell', 'realmArchive']) {
        if (p[key] !== undefined) card[key] = p[key];
      }
      return;
    }
    case SET_ANIMATE: {
      const card = draft.cards[command.payload.cardId];
      if (!card) return;
      const a = command.payload.animate;
      if (a) {
        card.type = a.type;
        card.subTypes = a.subTypes;
        card.baseAttack = a.baseAttack;
        card.baseLife = a.baseLife;
        card.currentAttack = a.currentAttack;
        card.currentLife = a.currentLife;
        card.enchantressAnimated = true;
        card.enchantressOriginal = a.original ?? null;
      } else {
        const snap = card.enchantressOriginal;
        if (snap) {
          card.type = snap.type;
          card.subTypes = snap.subTypes;
          card.baseAttack = snap.baseAttack;
          card.baseLife = snap.baseLife;
          card.currentAttack = snap.currentAttack;
          card.currentLife = snap.currentLife;
        }
        card.enchantressAnimated = false;
        card.enchantressOriginal = null;
      }
      return;
    }
    case SET_IDENTITY: {
      const card = draft.cards[command.payload.cardId];
      if (!card) return;
      const id = command.payload.identity;
      const FIELDS = ['cardId', 'name', 'imageUrl', 'imagePath', 'foiling', 'subTypes', 'elements', 'printedKeywords', 'movementBonus', 'rangedValue'];
      if (id) {
        if (id.original !== undefined) card.maskedOriginal = id.original;
        for (const f of FIELDS) if (id[f] !== undefined) card[f] = id[f];
        card.dragonlordBorrow = !!id.dragonlordBorrow;
      } else {
        const o = card.maskedOriginal;
        if (o) for (const f of FIELDS) if (o[f] !== undefined) card[f] = o[f];
        card.maskedOriginal = null;
        card.dragonlordBorrow = false;
      }
      return;
    }
    case SET_POSITION: {
      const card = draft.cards[command.payload.cardId];
      if (!card) return;
      const { col, row, x, z } = command.payload;
      card.cell = { col, row };
      // World coords are the AURA exception only (intersection anchors that no
      // cell can express). Cell-bound cards render from cell + stackSeq; their
      // commands carry no x/z, and any legacy stored pos is dropped on the
      // next placement so stale coords can never shadow the cell.
      if (typeof x === 'number' && typeof z === 'number') card.pos = { x, z };
      else delete card.pos;
      // Within-cell stacking order: monotonic per-match sequence. The resting
      // render cascades a cell's cards by stackSeq (pure fn of server state),
      // so both clients draw identical stacks without world coordinates. Every
      // placement re-bumps. The most recently placed card sits on top.
      draft.phase.stackCounter = (draft.phase.stackCounter || 0) + 1;
      card.stackSeq = draft.phase.stackCounter;
      return;
    }
    case SET_UNIT_HANDS: {
      // MERGE, never blind-replace: a client only ever holds the spell FACES it
      // is entitled to see, so the new controller of a just-stolen hand emits
      // BACKS. mergeUnitHandViews keeps any face the server already has (per
      // spell), so a back-emit can never wipe the authoritative faces. They
      // persist and project to the new controller. Whole-hand removal is driven
      // by the owner leaving play (MOVE_TO_ZONE above), not by an omitted emit.
      const prior = draft.unitHands || {};
      const priorSeen = {};
      for (const [oid, e] of Object.entries(prior)) {
        priorSeen[oid] = new Map((e.spells || []).map((s) => [s.id, s._seenBy || []]));
      }
      const merged = mergeUnitHandViews(command.payload.unitHands || {}, prior);
      // Carried stolen spells belong to a server-owned zone. A drag or a
      // sanitized client echo cannot remove or overwrite their membership.
      for (const c of Object.values(draft.cards || {})) {
        if (c.zone !== 'held' || !c.castLockOwnerId) continue;
        for (const entry of Object.values(merged)) entry.spells = (entry.spells || []).filter((s) => s.id !== c.instanceId);
        const oid = c.castLockOwnerId;
        if (!merged[oid]) merged[oid] = { controller: c.controller, spells: [] };
        merged[oid].spells.push({ id: c.instanceId, cardId: c.cardId,
          _castableOnlyBy: oid, _ownerSeat: c.controller, rotated: c.rotated,
          _controllerOnly: true, _returnToOwnerHand: true });
      }
      // Never adopt a client-asserted controller or reveal grant (#60): the
      // controller re-derives from the owner card's LIVE control, and a
      // _seenBy addition is only honored for that controller. A client
      // cannot grant the other seat a sticky reveal it never earned.
      for (const [oid, entry] of Object.entries(merged)) {
        const owner = draft.cards?.[oid];
        const controller = owner
          ? (owner.controller ?? (owner.rotated ? 'p2' : 'p1'))
          : entry.controller;
        entry.controller = controller;
        for (const s of entry.spells || []) {
          const prev = priorSeen[oid]?.get(s.id) || [];
          const inc = Array.isArray(s._seenBy) ? s._seenBy : [];
          s._seenBy = [...new Set([...prev, ...inc.filter((seat) => seat === controller)])];
        }
      }
      draft.unitHands = merged;
      return;
    }
    case SET_DISABLED_AUTOMATIONS: {
      // Per-seat per-card automation opt-out (#38). LEGACY: the per-card
      // toggle was replaced by the session-wide automation mode, so no
      // current client emits this. It stays accepted (rather than
      // throwing UnknownCommand) for clients installed before that change.
      // Whole-list replace; the seat gate lives in the command handler so
      // one seat can never edit the other's list.
      const { seat, cardIds } = command.payload || {};
      const s = seat === 'p2' ? 'p2' : 'p1';
      if (!draft.disabledAutomations) draft.disabledAutomations = { p1: [], p2: [] };
      draft.disabledAutomations[s] = Array.isArray(cardIds)
        ? cardIds.filter((x) => typeof x === 'string' && x.length < 120).slice(0, 500)
        : [];
      return;
    }
    case PASS_TURN: {
      let incoming = draft.phase.currentTurn === 'p1' ? 'p2' : 'p1';
      if (!draft.phase.turnNumbers) {
        draft.phase.turnNumbers = { p1: 0, p2: 0 };
      }
      // Skip-a-turn (Vaults of Zul): if the incoming player owns a card flagged
      // `skipNextTurn` (its Avatar), consume the flag and pass THROUGH. Their turn is
      // skipped, so play returns to the current player. Only ever set on an Avatar, so a
      // direct flag scan avoids a catalog type lookup in this pure reducer.
      const flagged = Object.values(draft.cards ?? {}).find(
        (c) => c.skipNextTurn && c.zone === 'play' && (c.rotated ? 'p2' : 'p1') === incoming,
      );
      if (flagged) {
        flagged.skipNextTurn = false;
        incoming = incoming === 'p1' ? 'p2' : 'p1';
      }
      // Turn boundary, atomic with the flip + server-authoritative: heal the
      // outgoing turn's minion damage, then untap the incoming seat's cards.
      // These used to ride separate client-fired turn intents that did not
      // reliably reach the server, so a tapped card stayed tapped server-side
      // and the next diff re-applied tapped=true on the client.
      //
      // A manual (automations-off) session skips both: the turn flips and
      // nothing else moves, so the players untap and heal by hand like they
      // would across a real table. `automations` is only ever false when the
      // session opted in. Undefined (a state dealt before the field existed)
      // keeps the automated boundary.
      const automated = draft.automations !== false;
      if (automated) healStep(draft);
      draft.phase.currentTurn = incoming;
      draft.phase.turnNumbers[incoming] += 1;
      draft.phase.globalTurn = (draft.phase.globalTurn || 0) + 1;
      draft.phase.currentPhase = 'start';
      if (automated) untapStep(draft, incoming);
      // Codex: only the game's first turn skips the draw. An unresolved
      // obligation from the outgoing turn is forfeited here, never carried.
      draft.phase.drawPending = automated && draft.phase.globalTurn > 1
        ? { seat: incoming, globalTurn: draft.phase.globalTurn }
        : null;
      return;
    }
    default:
      throw new UnknownCommand(command.type);
  }
}

// A DRAW or a marked drag-draw by the obligated seat satisfies the start-phase
// draw (valkenhall-qa#158). Keyed on the seat alone: which deck is the player's
// choice, and a manual draw must never leave a second draw owed.
function dischargeDrawObligation(draft, seat, fromZone) {
  if (fromZone !== 'spellbook' && fromZone !== 'atlas') return;
  if (draft.phase?.drawPending?.seat === seat) draft.phase.drawPending = null;
}

export function reduce(state, command) {
  const [next, patches, inversePatches] = produceWithPatches(state, (draft) =>
    applyCommand(draft, command),
  );
  return { next, patches, inversePatches };
}

// --- wire.js (vendored) ---
export function makeFull(version, projected) {
  return { kind: 'full', version, state: projected };
}

export function makeDiff(version, patches) {
  return { kind: 'diff', version, patches };
}

export function foldInbound(localVersion, localState, msg) {
  if (msg.kind === 'full') {
    return { state: msg.state, version: msg.version, needFull: false };
  }

  if (msg.kind === 'diff') {
    if (msg.version === localVersion + 1) {
      return {
        state: applyPatches(localState, msg.patches),
        version: msg.version,
        needFull: false,
      };
    }
    return { state: localState, version: localVersion, needFull: true };
  }

  return { state: localState, version: localVersion, needFull: true };
}
